import { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { getApiUrl } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
export default function BadgeCycles() {
     const [cycles, setCycles] = useState([]);
     const [loading, setLoading] = useState(false);
     const [branches, setBranches] = useState([]);
     const [technicians, setTechnicians] = useState([]);
     const token = sessionStorage.getItem("token");
     const currentYear = new Date().getFullYear();
     const [addModalOpen, setAddModalOpen] = useState(false);
     const [editModalOpen, setEditModalOpen] = useState(false);
     const [newCycle, setNewCycle] = useState({
          badgeCode: "",
          technicianName: "",
          branchName: "",
          year: currentYear,
          cyclePeriod: "",
          status: "active",
     });
     const handleAdd = (e) => {
          e.preventDefault();
     }

     const navigate = useNavigate();
     // fetch branches and technicians for form selects
     useEffect(() => {
          const fetchBranches = async () => {
               try {
                    const apiUrl = getApiUrl("/getBranches");
                    const res = await fetch(apiUrl, {
                         headers: {
                              "Authorization": `Bearer ${token}`,
                         },
                    });
                    if (!res.ok) throw new Error("Failed to fetch branches");
                    const data = await res.json();
                    // FIX: Use data.data if present
                    setBranches(Array.isArray(data.data) ? data.data : []);
               } catch (err) {
                    console.error("Error fetching branches:", err);
               }
          };
          const fetchTechnicians = async () => {
               try {
                    const apiUrl = getApiUrl("/getTechnicians");
                    const res = await fetch(apiUrl, {
                         headers: {
                              "Authorization": `Bearer ${token}`,
                         },
                    });
                    if (!res.ok) throw new Error("Failed to fetch technicians");
                    const data = await res.json();
                    setTechnicians(Array.isArray(data.data) ? data.data : []); // ✅ Extract data.data
               } catch (err) {
                    console.error("Error fetching technicians:", err);
                    setTechnicians([]); // Set empty array on error
               }
          };

          fetchBranches();
          fetchTechnicians();
     }, [token]);

     const [formData, setFormData] = useState({
          badge_id: "",
          techni_id: "",
          branch_id: "",
          year: new Date().getFullYear(),
          quarter: "Q1",
          status: "ISSUED",
     });
     const [editingId, setEditingId] = useState(null);
     const [filters, setFilters] = useState({
          year: currentYear, // Set default year
          quarter: "",
          branch_id: "",
          status: "",
     });
     const [page, setPage] = useState(1);
     const [totalPages, setTotalPages] = useState(1);

     const quarters = [
          { value: "Q1", label: "Jan–Mar" },
          { value: "Q2", label: "Apr–Jun" },
          { value: "Q3", label: "Jul–Sep" },
          { value: "Q4", label: "Oct–Dec" },
     ];

     // Fetch cycles with filters + pagination
     const fetchCycles = async () => {
          setLoading(true);
          try {
               const token = sessionStorage.getItem("token");
               const params = new URLSearchParams({
                    page,
                    year: filters.year,
                    quarter: filters.quarter,
                    branch_id: filters.branch_id,
                    status: filters.status,
               });
               const apiUrl = getApiUrl(`/getBadgeCycles?${params.toString()}`);
               const res = await fetch(apiUrl, {
                    headers: {
                         "Authorization": `Bearer ${token}`,
                    },
               });
               if (!res.ok) throw new Error("Failed to fetch cycles");
               const data = await res.json();
               setCycles(data.data);
               setTotalPages(data.total_pages);
          } catch (err) {
               console.error("Error fetching cycles:", err);
          }
          setLoading(false);
     };

     useEffect(() => {
          fetchCycles();
          // eslint-disable-next-line
     }, [page, filters]);

     // Handle form submit
     const handleSubmit = async (e) => {
          e.preventDefault();
          try {
               const token = localStorage.getItem("token");
               const method = editingId ? "PUT" : "POST";
               const url = editingId
                    ? `/api/badge_cycles/${editingId}`
                    : `/api/badge_cycles`;
               const res = await fetch(url, {
                    method,
                    headers: {
                         "Content-Type": "application/json",
                         "Authorization": `Bearer ${token}`,
                    },
                    body: JSON.stringify(formData),
               });
               if (!res.ok) throw new Error("Failed to save cycle");
               setFormData({
                    badge_id: "",
                    techni_id: "",
                    branch_id: "",
                    year: new Date().getFullYear(),
                    quarter: "Q1",
                    status: "ISSUED",
               });
               setEditingId(null);
               fetchCycles();
          } catch (err) {
               console.error("Error saving cycle:", err);
          }
     };

     // Handle edit
     const handleEdit = (cycle) => {
          setFormData({
               badge_id: cycle.badge_id,
               techni_id: cycle.techni_id,
               branch_id: cycle.branch_id,
               year: cycle.year,
               quarter: cycle.quarter,
               status: cycle.status,
          });
          setEditingId(cycle.cycle_id);
     };

     // Handle delete
     const handleDelete = async (id) => {
          if (!window.confirm("Are you sure you want to delete this cycle?")) return;
          try {
               const token = localStorage.getItem("token");
               const res = await fetch(`/api/badge_cycles/${id}`, {
                    method: "DELETE",
                    headers: {
                         "Authorization": `Bearer ${token}`,
                    },
               });
               if (!res.ok) throw new Error("Failed to delete cycle");
               fetchCycles();
          } catch (err) {
               console.error("Error deleting cycle:", err);
          }
     };

     const handleDownloadExcel = () => {
          if (!cycles || cycles.length === 0) {
               toast.info("No data to export.");
               return;
          }
          // Prepare data for Excel
          const exportData = cycles.map((c) => ({
               "ID": c.cycle_id,
               "Badge Code": c.badge_code || c.badge_id,
               "Technician": c.technician_name || c.techni_id,
               "Branch": c.branch_name || c.branch_id,
               "Year": c.year,
               "Cycle Period": c.cycle_period,
               "Status": c.status,
          }));
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "BadgeCycles");
          const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
          const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
          saveAs(blob, "badge_cycles.xlsx");
     };

     return (
          <div>
               <ToastContainer position="top-right" />

               <div className=" d-flex justify-content-between align-items-center">
                    <h2 className="h4">Badge Cycles Management</h2>
                    <div className="col-auto d-flex">
                         <button
                              onClick={handleDownloadExcel}
                              className="btn btn-success mr-2"
                         >
                              <i className="fas fa-file-excel mr-1"></i> Download Excel
                         </button>
                         <button
                              onClick={() => navigate("/badge-cycles/new")}
                              className="btn btn-primary"
                         >
                              <i className="fas fa-plus mr-1"></i>
                         </button>
                    </div>
               </div>

               <div className="modal" style={{ display: addModalOpen ? 'block' : 'none' }}>
                    <div className="modal-dialog modal-xl">
                         <div className="modal-content">
                              <div className="modal-header">
                                   <h5 className="modal-title">Add New Badge Cycle</h5>
                                   <button type="button" className="close" onClick={() => setAddModalOpen(false)}>
                                        <span>&times;</span>
                                   </button>
                              </div>
                              <div className="modal-body">
                                   {/* need to show technicians list belong to the selected branch */}
                                   <div className="form-group">
                                        <label htmlFor="branchSelect">Select Branch</label>
                                        <select
                                             id="branchSelect"
                                             className="form-control"
                                             value={newCycle.branchId || ""}
                                             onChange={(e) => setNewCycle({ ...newCycle, branchId: e.target.value, technicianId: "" })}
                                             required
                                        >
                                             <option value="">Select a branch</option>
                                             {branches.map((branch) => (
                                                  <option key={branch.branch_id} value={branch.branch_id}>
                                                       {branch.branch_name}
                                                  </option>
                                             ))}
                                        </select>
                                   </div>

                              </div>
                         </div>
                    </div>
               </div>
               {/* Edit modal */}

               <div className="card shadow mt-2 mb-4">
                    <div className="card-header">
                         <div className="row">
                              <div className="col-auto ">
                                   <input
                                        type="number"
                                        placeholder="Year"
                                        min={2025}
                                        value={filters.year || currentYear}
                                        onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                                        className="form-control"
                                   />
                              </div>
                              <div className="col-auto ">
                                   <select
                                        value={filters.quarter}
                                        onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
                                        className="form-control"
                                   >
                                        <option value="">All Quarters</option>
                                        {quarters.map((q) => (
                                             <option key={q.value} value={q.value}>
                                                  {q.label}
                                             </option>
                                        ))}
                                   </select>
                              </div>
                              <div className="col-auto ">
                                   <select
                                        value={filters.branch_id}
                                        onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
                                        className="form-control"
                                   >
                                        <option value="">All Branches</option>
                                        {Array.isArray(branches) && branches.map((b) => (
                                             <option key={b.branch_id} value={b.branch_id}>
                                                  {b.branch_name}
                                             </option>
                                        ))}
                                   </select>
                              </div>
                              <div className="col-auto ">
                                   <select
                                        value={filters.status}
                                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                        className="form-control"
                                   >
                                        <option value="">All Status</option>
                                        <option value="ISSUED">ISSUED</option>
                                        <option value="RETURNED">RETURNED</option>
                                        <option value="SENT_TO_VENDOR">SENT_TO_VENDOR</option>
                                        <option value="ANALYZED">ANALYZED</option>
                                   </select>
                              </div>
                              <div className="col-auto ">
                                   <button
                                        onClick={() => {
                                             setFilters({ year: "", quarter: "", branch_id: "", status: "" });
                                             setPage(1);
                                        }}
                                        className="btn btn-secondary w-100"
                                   >
                                        Reset
                                   </button>
                              </div>

                         </div>
                    </div>
                    <div className="card-body">
                         {loading ? (
                              <p>Loading...</p>
                         ) : cycles.length === 0 ? (
                              <div className="alert alert-info text-center">
                                   No badge cycles found. Try adjusting your filters.
                              </div>
                         ) : (
                              <table className="table table-bordered">
                                   <thead className="thead-light">
                                        <tr>
                                             <th>ID</th>
                                             <th>Badge Code</th>
                                             <th>Technician</th>
                                             <th>Branch</th>
                                             <th>Year</th>
                                             <th>Cycle Period</th>
                                             <th>Status</th>

                                        </tr>
                                   </thead>
                                   <tbody>
                                        {cycles.map((c) => (
                                             <tr key={c.cycle_id}>
                                                  <td>{c.cycle_id}</td>
                                                  <td>{c.badge_code || c.badge_id}</td>
                                                  <td>{c.technician_name || c.techni_id}</td>
                                                  <td>{c.branch_name || c.branch_id}</td>
                                                  <td>{c.year}</td>
                                                  <td>{c.cycle_period}</td>
                                                  <td>{c.status}</td>

                                             </tr>
                                        ))}
                                   </tbody>
                              </table>
                         )}
                         <div className="d-flex justify-content-center mt-4">
                              <button
                                   disabled={page <= 1}
                                   onClick={() => setPage(page - 1)}
                                   className="btn btn-secondary mx-1"
                              >
                                   Prev
                              </button>
                              <span className="align-self-center px-3">Page {page} of {totalPages}</span>
                              <button
                                   disabled={page >= totalPages}
                                   onClick={() => setPage(page + 1)}
                                   className="btn btn-secondary mx-1"
                              >
                                   Next
                              </button>
                         </div>
                    </div>
               </div>


          </div>
     );
}